// Progressive enhancement only: all saves, previews and validation work server-side.
export function initAdmin(): void {
  const form = document.querySelector<HTMLFormElement>("[data-editor-form]");
  if (!form) return;
  let dirty = false;
  const state = form.querySelector<HTMLElement>("[data-save-state]");
  const body = form.querySelector<HTMLTextAreaElement>("#body");
  const count = form.querySelector<HTMLElement>("[data-word-count]");
  const markDirty = () => {
    dirty = true;
    if (state) state.textContent = "有未保存修改 · 不会自动保存";
  };
  const updateCount = () => {
    if (!count || !body) return;
    const template = document.createElement("template");
    template.innerHTML = body.value;
    const text = body.hasAttribute("data-rich-editor") ? template.content.textContent ?? "" : body.value;
    count.textContent = `${Array.from(text.replace(/\s/g, "")).length.toLocaleString()} 字`;
  };
  form.addEventListener("input", () => { markDirty(); updateCount(); });
  form.addEventListener("change", markDirty);
  form.addEventListener("richtext:ready", updateCount);
  updateCount();
  window.addEventListener("beforeunload", (event) => {
    if (!dirty) return;
    event.preventDefault();
    event.returnValue = "";
  });
  // Validation errors represent unsaved input too, including a failed first save.
  const errors = document.querySelector<HTMLElement>("[data-error-summary]");
  if (errors) {
    markDirty();
    errors.querySelectorAll<HTMLElement>("[data-error-field]").forEach((link) => {
      const field = link.dataset.errorField ?? "";
      const input = form.elements.namedItem(field) ?? document.getElementById(field);
      if (!(input instanceof HTMLElement)) return;
      input.setAttribute("aria-invalid", "true");
      input.setAttribute("aria-describedby", `${input.getAttribute("aria-describedby") ?? ""} ${errors.id}`.trim());
      link.addEventListener("click", (event) => {
        const editable = input.closest("[data-rich-editor-container]")?.querySelector<HTMLElement>('.ck-editor__editable[contenteditable="true"]');
        if (!editable) return;
        event.preventDefault();
        editable.scrollIntoView({ block: "center" });
        editable.focus();
      });
    });
    errors.focus();
  }

  const category = form.querySelector<HTMLSelectElement>("#category");
  const topic = form.querySelector<HTMLSelectElement>("#guide_topic");
  const updateTopic = () => {
    if (!category || !topic) return;
    if (category.value !== "guide") topic.value = "";
    topic.disabled = category.value !== "guide";
  };
  category?.addEventListener("change", updateTopic);
  updateTopic();

  const cover = form.querySelector<HTMLInputElement>("#cover_image");
  const coverPreview = form.querySelector<HTMLImageElement>("[data-cover-preview]");
  const coverStatus = form.querySelector<HTMLElement>("[data-cover-status]");
  const coverCancel = form.querySelector<HTMLButtonElement>("[data-cover-cancel]");
  let coverURL = "";
  const updateCover = () => {
    if (!cover || !coverPreview || !coverStatus || !coverCancel) return;
    if (coverURL) URL.revokeObjectURL(coverURL);
    coverURL = "";
    coverPreview.hidden = true;
    coverPreview.removeAttribute("src");
    cover.setCustomValidity("");
    cover.removeAttribute("aria-invalid");
    const file = cover.files?.[0];
    coverCancel.hidden = !file;
    if (!file) {
      coverStatus.textContent = "未选择新图片，保留封面地址。";
      return;
    }
    let error = "";
    if (file.size > 5 * 1024 * 1024) error = "封面图片不能超过 5 MB。";
    else if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) error = "请选择 JPG、PNG 或 GIF 图片。";
    if (error) {
      cover.setCustomValidity(error);
      cover.setAttribute("aria-invalid", "true");
      coverStatus.textContent = error;
      return;
    }
    coverURL = URL.createObjectURL(file);
    coverPreview.src = coverURL;
    coverPreview.hidden = false;
    coverStatus.textContent = `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB · ${cover.dataset.uploadAction ?? "保存或预览时上传"}`;
  };
  cover?.addEventListener("change", updateCover);
  coverCancel?.addEventListener("click", () => {
    if (cover) cover.value = "";
    updateCover();
    markDirty();
    cover?.focus();
  });
  // Restore file previews after back-forward navigation without clearing server errors.
  if (cover?.files?.length) updateCover();

  const dialog = document.querySelector<HTMLDialogElement>("[data-confirm-dialog]");
  let confirmed: HTMLButtonElement | null = null;
  form.addEventListener("submit", (event) => {
    const button = event.submitter;
    if (!(button instanceof HTMLButtonElement)) {
      // Enter in an input defaults to a safe draft save, never publication.
      event.preventDefault();
      const save = form.querySelector<HTMLButtonElement>('button[value="draft"], button[value="save"]');
      if (save) form.requestSubmit(save);
      return;
    }
    if (button.value === "preview") return;
    if (button.dataset.confirm && confirmed !== button && dialog) {
      event.preventDefault();
      const message = dialog.querySelector("[data-confirm-message]");
      if (message) message.textContent = button.dataset.confirm;
      dialog.returnValue = "cancel";
      dialog.addEventListener("close", () => {
        if (dialog.returnValue === "confirm") {
          confirmed = button;
          form.requestSubmit(button);
          confirmed = null;
        } else button.focus();
      }, { once: true });
      dialog.showModal();
      return;
    }
    dirty = false;
    // Disabled submitters are excluded from form serialization, so retain action.
    const action = document.createElement("input");
    action.type = "hidden";
    action.name = "action";
    action.value = button.value;
    form.append(action);
    form.querySelectorAll<HTMLButtonElement>('button[type="submit"]').forEach((item) => { item.disabled = true; });
    if (state) state.textContent = "正在保存修订，请稍候…";
    form.setAttribute("aria-busy", "true");
  });
  // Back-forward cache restores the document, including disabled controls.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    form.querySelectorAll<HTMLInputElement>('input[name="action"]').forEach((input) => input.remove());
    form.querySelectorAll<HTMLButtonElement>('button[type="submit"]').forEach((button) => { button.disabled = false; });
    form.removeAttribute("aria-busy");
    markDirty();
  });
}
